import { useCallback, useEffect, useState } from 'react'
import { Badge, Modal, Skeleton } from '@indipia/ui-core'
import { getAssignEvidence, type AssignEvidence, type AssignEvidenceCandidate } from '../api/foremanApi'
import { ErrorBanner } from './AsyncState'

// 자동매칭 근거 팝업 — 간트 상세창 '자동매칭' 배지·시공반장 팝업 '자동배정 ⓘ' 배지가 같이 쓴다.
// 구성(순서 고정): 머리 → 요약 박스 → 배정 조건 → 후보 순위(2명 이상일 때) / available=false 는 이유 한 줄만.
// 순위는 점수가 아니라 우선순위(룰정의서 930 #2): 최근 12개월 협업 횟수 → 최근 협업일 → 등록일. 별점 · 보고준수 · 하자는 참고 값.

interface Props {
  open: boolean
  onClose: () => void
  taskId: number
  /** 머리 둘째 줄의 반장 이름(응답에는 반장 이름이 없다) */
  partnerName?: string | null
}

const label12: React.CSSProperties = { fontSize: 12, color: 'var(--shell-on-surface-2)' }

/** 율(0~1) → 'N%'. 없으면 '-' */
function formatRate(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '-'
  return `${Math.round(n * 100)}%`
}

/** 'yyyy-MM-dd' → 'M/D'. 없으면 '-' */
function formatMonthDay(iso: string | null | undefined): string {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
  return m ? `${Number(m[2])}/${Number(m[3])}` : '-'
}

const UNAVAILABLE_TEXT: Record<string, string> = {
  LEGACY_VERSION: '예전 방식으로 배정돼 근거를 보여드릴 수 없어요',
}

function CandidateRow({ c }: { c: AssignEvidenceCandidate }) {
  const dim = c.scheduleConflict
  return (
    <li style={{ padding: '6px 0', opacity: dim ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13, color: 'var(--shell-on-surface)', textDecoration: dim ? 'line-through' : 'none' }}>{c.partnerName}</b>
        <span style={{ fontSize: 13, color: 'var(--shell-on-surface)', textDecoration: dim ? 'line-through' : 'none' }}>{`· 협업 ${c.collabCount ?? 0}회 · 최근 협업 ${formatMonthDay(c.lastCollabDate)}`}</span>
        {c.selected && <Badge status="success">배정</Badge>}
        {c.scheduleConflict && <Badge status="mute">일정 겹침</Badge>}
      </div>
      <div style={{ ...label12, textDecoration: dim ? 'line-through' : 'none' }}>
        {`참고 : 별점 ${c.avgRating != null ? c.avgRating.toFixed(1) : '-'} · 보고준수 ${formatRate(c.reportRate)} · 하자 ${formatRate(c.defectRate)}`}
      </div>
    </li>
  )
}

export function AssignEvidenceModal({ open, onClose, taskId, partnerName }: Props) {
  const [data, setData] = useState<AssignEvidence | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setError(false)
    try {
      setData(await getAssignEvidence(taskId))
    } catch {
      setData(null); setError(true)
    } finally {
      setLoading(false)
    }
  }, [taskId])

  useEffect(() => {
    if (!open) return
    setData(null)
    void load()
  }, [open, load])

  const ev = data?.evidence ?? null
  const name = partnerName ?? ''
  const subtitle = [data?.workTypeName, name].filter(Boolean).join(' · ')

  return (
    <Modal open={open} onClose={onClose} title="자동매칭 근거" size="md">
      <div onMouseDown={e => e.stopPropagation()}>
        {subtitle && <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{subtitle}</div>}
        <div style={{ ...label12, marginBottom: 10 }}>시공파트너 자동배정 근거</div>

        {loading && (
          <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map(i => <Skeleton key={i} height={28} />)}
          </div>
        )}
        {!loading && error && <ErrorBanner message="자동매칭 근거를 불러오지 못했어요" onRetry={() => void load()} />}

        {!loading && !error && data && !data.available && (
          <div style={{ fontSize: 13, color: 'var(--shell-on-surface)' }} role="status">
            {UNAVAILABLE_TEXT[data.unavailableReason ?? ''] ?? '자동매칭 근거가 없어요'}
          </div>
        )}

        {!loading && !error && data?.available && ev && (
          <>
            {/* 요약 박스 */}
            <div style={{ padding: '10px 12px', borderRadius: 12, background: 'var(--shell-bg)', border: 'var(--border-width-hairline) solid var(--shell-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{ev.summary.headline}</div>
              {ev.summary.detail && <div style={{ ...label12, marginTop: 2 }}>{ev.summary.detail}</div>}
            </div>

            {/* 배정 조건 */}
            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface-2)' }}>배정 조건</div>
            <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {ev.conditions.map(cd => (
                <li key={cd.code} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <span aria-hidden="true" style={{ width: 14, flexShrink: 0, color: cd.passed === true ? 'var(--status-success-600)' : 'var(--shell-hint)', fontWeight: 700 }}>
                    {cd.passed === true ? '✓' : cd.passed === null ? '–' : '✕'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 13, color: 'var(--shell-on-surface)' }}>{cd.label}</span>
                    {cd.detail && <span style={{ ...label12, marginLeft: 6 }}>{cd.detail}</span>}
                  </div>
                </li>
              ))}
            </ul>

            {/* 후보 순위 */}
            {ev.showCandidates && (
              <>
                <div style={{ marginTop: 14, fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface-2)' }}>후보 순위</div>
                <ul style={{ listStyle: 'none', margin: '2px 0 0', padding: 0 }}>
                  {ev.candidates.map(c => <CandidateRow key={c.partnerId} c={c} />)}
                </ul>
                <div style={{ ...label12, marginTop: 6 }}>
                  순위 기준 : 최근 12개월 협업 횟수 → 최근 협업일 → 등록일 (별점 · 보고준수 · 하자는 참고)
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
